import { Crosshair, Trash2 } from 'lucide-react';
import { useRef, useState } from 'react';

import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

export interface MeasurementRowProps {
    id: string;
    name: string;
    description?: string;
    value: number;
    unit: string;
    isSelected: boolean;
    onSelect: (id: string) => void;
    onLocate?: () => void;
    onDelete?: () => void;
    onRename?: (newName: string) => void;
}

export function MeasurementRow({
    id,
    name,
    description,
    value,
    unit,
    isSelected,
    onSelect,
    onLocate,
    onDelete,
    onRename,
}: MeasurementRowProps) {
    const [isRenaming, setIsRenaming] = useState(false);
    const [editName, setEditName] = useState(name);
    const inputRef = useRef<HTMLInputElement>(null);

    const handleStartRename = (e: React.MouseEvent) => {
        if (!onRename) return;
        e.stopPropagation();
        setEditName(name);
        setIsRenaming(true);
        setTimeout(() => inputRef.current?.select(), 0);
    };

    const handleCommitRename = () => {
        setIsRenaming(false);
        const trimmed = editName.trim();
        if (trimmed && trimmed !== name) {
            onRename?.(trimmed);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            handleCommitRename();
        } else if (e.key === 'Escape') {
            setIsRenaming(false);
        }
    };

    return (
        <div
            className={cn(
                'group flex cursor-pointer items-center gap-1 px-2 py-1 text-xs transition-colors duration-200 border-l-4',
                isSelected
                    ? 'border-(--group-color) text-foreground'
                    : 'border-transparent text-muted-foreground hover:bg-muted hover:text-foreground'
            )}
            onClick={() => !isRenaming && onSelect(id)}
        >
            {isRenaming ? (
                <input
                    ref={inputRef}
                    className="min-w-0 flex-1 rounded border border-border bg-background px-1 font-medium text-foreground outline-none focus:border-primary"
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    onBlur={handleCommitRename}
                    onKeyDown={handleKeyDown}
                    onClick={(e) => e.stopPropagation()}
                />
            ) : (
                <Tooltip delayDuration={550}>
                    <TooltipTrigger asChild>
                        <span
                            className={cn(
                                'min-w-0 flex-1 truncate',
                                'font-medium',
                                onRename && 'cursor-text'
                            )}
                            onDoubleClick={handleStartRename}
                            title={onRename ? 'Double-click to rename' : undefined}
                        >
                            {name}
                        </span>
                    </TooltipTrigger>
                    {description && <TooltipContent side="top">{description}</TooltipContent>}
                </Tooltip>
            )}
            {onDelete && (
                <button
                    className="shrink-0 p-0.5 opacity-0 group-hover:opacity-100"
                    onClick={(e) => {
                        e.stopPropagation();
                        onDelete();
                    }}
                    title="Remove"
                >
                    <Trash2 className="h-3 w-3 hover:text-destructive transition-all duration-200" />
                </button>
            )}
            {onLocate ? (
                <button
                    className="shrink-0 rounded p-0.5 opacity-0 hover:bg-muted group-hover:opacity-100"
                    onClick={(e) => {
                        e.stopPropagation();
                        onLocate();
                    }}
                    title="Locate"
                >
                    <Crosshair className="h-3 w-3" />
                </button>
            ) : (
                <div className="w-4 shrink-0" />
            )}
            <span className="w-20 shrink-0 text-right tabular-nums font-mono">
                {value.toFixed(1)} {unit}
            </span>
        </div>
    );
}
